
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TokenPair struct
 */
class TokenPair {
  /**
   * Initialize struct
   * @param {!number=} pairId
   * @param {!string=} accessToken
   * @param {!string=} refreshToken
   * @constructor
   */
  constructor (pairId = new UInt64(0, 0), accessToken = '', refreshToken = '') {
    this.PairId = pairId
    this.AccessToken = accessToken
    this.RefreshToken = refreshToken
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TokenPair}
   * @param {!TokenPair} other Other struct
   * @returns {!TokenPair} This struct
   */
  copy (other) {
    if (other.PairId != null) {
      this.PairId = UInt64.fromNumber(other.PairId)
    } else {
      this.PairId = null
    }
    if (other.AccessToken != null) {
      this.AccessToken = other.AccessToken
    } else {
      this.AccessToken = null
    }
    if (other.RefreshToken != null) {
      this.RefreshToken = other.RefreshToken
    } else {
      this.RefreshToken = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TokenPair}
   * @returns {!TokenPair} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TokenPairModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TokenPairModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TokenPair}
   * @param {!TokenPair} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TokenPair)) {
      throw new TypeError('Instance of TokenPair is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TokenPair}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      PairId: ((this.PairId != null) ? this.PairId.toNumber() : null),
      AccessToken: ((this.AccessToken != null) ? this.AccessToken : null),
      RefreshToken: ((this.RefreshToken != null) ? this.RefreshToken : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TokenPair.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TokenPair} other Object value
   * @returns {!TokenPair} Created struct
   */
  static fromObject (other) {
    return new TokenPair().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TokenPair}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TokenPair.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TokenPair}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 14102
  }
}

export { TokenPair };

/**
 * Fast Binary Encoding TokenPair field model
 */
class FieldModelTokenPair extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.PairId = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.AccessToken = new fbe.FieldModelString(buffer, this.PairId.fbeOffset + this.PairId.fbeSize)
    this.RefreshToken = new fbe.FieldModelString(buffer, this.AccessToken.fbeOffset + this.AccessToken.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTokenPair}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTokenPair}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.PairId.fbeSize + this.AccessToken.fbeSize + this.RefreshToken.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTokenPair}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.PairId.fbeExtra + this.AccessToken.fbeExtra + this.RefreshToken.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTokenPair}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTokenPair.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTokenPair}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 14102
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTokenPair}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTokenPair}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.PairId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PairId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PairId.fbeSize

    if ((fbeCurrentSize + this.AccessToken.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccessToken.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccessToken.fbeSize

    if ((fbeCurrentSize + this.RefreshToken.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RefreshToken.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RefreshToken.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTokenPair}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTokenPair}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTokenPair}
   * @param {!TokenPair} fbeValue Default value, defaults is new TokenPair()
   * @returns {!TokenPair} TokenPair value
   */
  get (fbeValue = new TokenPair()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTokenPair}
   * @param {!TokenPair} fbeValue TokenPair value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.PairId.fbeSize) <= fbeStructSize) {
      fbeValue.PairId = this.PairId.get()
    } else {
      fbeValue.PairId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PairId.fbeSize

    if ((fbeCurrentSize + this.AccessToken.fbeSize) <= fbeStructSize) {
      fbeValue.AccessToken = this.AccessToken.get()
    } else {
      fbeValue.AccessToken = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccessToken.fbeSize

    if ((fbeCurrentSize + this.RefreshToken.fbeSize) <= fbeStructSize) {
      fbeValue.RefreshToken = this.RefreshToken.get()
    } else {
      fbeValue.RefreshToken = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RefreshToken.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTokenPair}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTokenPair}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTokenPair}
   * @param {!TokenPair} fbeValue TokenPair value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTokenPair}
   * @param {TokenPair} fbeValue TokenPair value
   */
  setFields (fbeValue) {
    this.PairId.set(fbeValue.PairId)
    this.AccessToken.set(fbeValue.AccessToken)
    this.RefreshToken.set(fbeValue.RefreshToken)
  }
}

export { FieldModelTokenPair };

/**
 * Fast Binary Encoding TokenPair model
 */
class TokenPairModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTokenPair(this.buffer, 4)
  }

  /**
   * Get the TokenPair model
   * @this {!TokenPairModel}
   * @returns {!FieldModelTokenPair} model TokenPair model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TokenPairModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TokenPairModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TokenPairModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TokenPairModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTokenPair.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TokenPairModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TokenPairModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TokenPairModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TokenPairModel}
   * @param {!TokenPair} value TokenPair value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TokenPairModel}
   * @param {!TokenPair} value TokenPair value, defaults is new TokenPair()
   * @return {!object} Deserialized TokenPair value and its size
   */
  deserialize (value = new TokenPair()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TokenPair(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TokenPair(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TokenPairModel}
   * @param {!number} prev Previous TokenPair model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TokenPairModel };
